import { Container, Divider, FormControl, FormLabel, Heading, Input, SkeletonText, Switch } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import CardForm from 'components/ui/CardForm';
import FixedBar from 'components/ui/FixedBar';
import FormControlGroup from 'components/ui/FormControlGroup';
import HeaderBarInternal from 'components/ui/HeaderBarInternal';
import SubmitButton from 'components/ui/SubmitButton';
import { useMessages } from 'hooks/useMessages';
import { FormProvider, Resolver, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import CompanyHeader from '../components/CompanyHeader';
import SelectLists from '../components/SelectLists';
import { useCompany } from '../hooks/useCompany';
import { useCompanyRecipient } from '../hooks/useCompanyRecipient';
import { RecipientInputs, useSaveCompanyRecipient } from '../hooks/useSaveCompanyRecipient';

const validationSchema = Yup.object().shape({
  email: Yup.string().email().required(),
});

function Form({ recipient, company }: any) {
  const navigate = useNavigate();

  const defaultValues: RecipientInputs = {
    name: recipient?.name,
    email: recipient?.email,
    phone: recipient?.phone,
    active: recipient?.active,
    listIds: recipient?.lists.map(list => list.id),
  };

  const methods = useForm<RecipientInputs>({
    mode: 'all',
    resolver: yupResolver(validationSchema) as Resolver<RecipientInputs>,
    defaultValues
  });

  const { register, handleSubmit, reset, watch, formState: { errors, isValid } } = methods;

  const [isActive] = watch(['active']);

  const { onError, onSuccess } = useMessages({ success: 'Membro alterado com sucesso!' });

  const { 
    onSaveCompanyRecipient, 
    isPending,
  } = useSaveCompanyRecipient({ 
        recipientId: recipient.id,
        companyId: company.id,
        onSuccess: ({ data: { recipient } }) => {
          reset();
          onSuccess();
          navigate(`/clientes/${company.id}/perfil/membros`)
        },
        onError: (error) => onError(error?.response?.data?.message)
      });
      
  const onSubmit: SubmitHandler<RecipientInputs> = (data) => onSaveCompanyRecipient(data);
  
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Heading as="h1" size="h1">
          Dados do membro
          <span>Informações gerais</span>
        </Heading>

        <CardForm>
          <FormControl isInvalid={!!errors.name}>
            <FormLabel>Nome</FormLabel>
            <Input {...register('name')} />
          </FormControl>

          <FormControlGroup>
            <FormControl isInvalid={!!errors.email} isRequired>
              <FormLabel>E-mail</FormLabel>
              <Input {...register('email')} />
            </FormControl>
            <FormControl isInvalid={!!errors.phone}>
              <FormLabel>Celular</FormLabel>
              <Input {...register('phone')} />
            </FormControl>
          </FormControlGroup>

          <FormControl isInvalid={!!errors.active}>
            <FormLabel>Status</FormLabel>
            <Switch {...register('active')}>
            { isActive ? 'Ativo' : 'Inativo' }
            </Switch>
          </FormControl>

        </CardForm>

        <Divider mt="48px" mb="60px" />

        <Heading as="h1" size="h1">
          Listas
          <span>Em quais listas o membro está incluído</span>
        </Heading>

        <CardForm>
        { company?.lists && <SelectLists lists={company.lists} /> }
        </CardForm>

        <FixedBar>
          <SubmitButton 
            ml="auto"
            isDisabled={isPending || !isValid}
            isLoading={isPending}
            hideIcon
          >
            Salvar
          </SubmitButton>
        </FixedBar>
      </form>
    </FormProvider>
  );
}

export default function PageEditCompanyRecipient() {
  const { companyId, id } = useParams();
  const { isFetched: isFetchedCompany, data: company } = useCompany(companyId);
  const { isFetched: isFetchedRecipient,  data: recipient } = useCompanyRecipient(id);

  const isLoaded = isFetchedRecipient && isFetchedCompany;
  
  return (
    <>
      <HeaderBarInternal
        title="Membro"
        backTo={`/clientes/${companyId}/perfil/membros`}
      >
        <CompanyHeader company={company} />
      </HeaderBarInternal>
      
      <Divider />

      <Container 
        variant="internal"
        flexDirection="column" 
        mb="100px"
      >
        
        <SkeletonText isLoaded={isLoaded}>
        { isLoaded && <Form company={company} recipient={recipient} /> }
        </SkeletonText>
        
      </Container>
    </>
  )
}
